<template>
  <div class="split-view-container">
    <BreadcrumbHeader :breadcrumbs="breadcrumbs" :entity-id="props.entityId" :has-children="!!children.length" />
    <app-loading :loading="loading" />
    <div class="split-view" v-if="!loading">
      <div class="left" :style="!showRightPanel && 'width: 100%;'">
        <ImagesHeader :coverUrl="coverImageUrl" :logoUrl="logoUrl" />
        <div>
          <div class="meta-header">
            <div>
              <h1 class="name">{{ name }}</h1>
              <div class="address">{{ addressText }}</div>
            </div>
            <b-button
              v-b-modal="entityType === 'company' ? 'company-modal' : 'divisions-modal'"
              variant="outline-primary"
              class="edit-button"
            >
              <svg
                class="edit-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.555 7.57697C21.465 7.36097 21.335 7.16797 21.173 7.00897L16.999 2.83497C16.836 2.66797 16.644 2.53797 16.423 2.44497C15.993 2.26697 15.512 2.26497 15.072 2.44697C14.856 2.53797 14.664 2.66797 14.509 2.82697L3.25599 14.079C3.09499 14.242 2.96799 14.432 2.88099 14.643C2.79299 14.857 2.74899 15.082 2.74999 15.309V19.499C2.74999 19.967 2.93199 20.406 3.26299 20.736C3.58799 21.063 4.03999 21.25 4.49999 21.25H8.68999C8.91899 21.25 9.14299 21.206 9.35599 21.118C9.56599 21.032 9.75499 20.906 9.92199 20.741L21.171 9.49397C21.337 9.32897 21.466 9.13697 21.551 8.93197C21.643 8.71597 21.69 8.48697 21.69 8.25097C21.69 8.01497 21.643 7.78497 21.555 7.57697ZM8.58599 19.25H4.74999V15.414L12.75 7.41397L16.586 11.25L8.58599 19.25ZM18 9.83597L14.164 5.99997L15.75 4.41397L19.586 8.24997L18 9.83597Z"
                />
              </svg>
              Edit {{ entityType }}
            </b-button>
          </div>
          <p class="description">
            {{ description }}
          </p>
          <!-- TODO wire up to api -->
          <div class="tags">
            <div v-for="tag in tags" :key="tag" class="tag">
              {{ tag }}
            </div>
          </div>
        </div>
        <div class="division-info-container" v-if="showRightPanelBar">
          <FeedbackInfo :reviews="reviews" :class="showReviews && 'selected'" />
          <RolesInfo :numRoles="roles?.length" :class="showRolesSection && 'selected'" />
        </div>
        <div class="inline-header" v-if="entityType !== 'site'">
          <h2 class="number-of-children">
            {{ children?.length }} {{ childType }}{{ children?.length === 1 ? '' : 's' }}
          </h2>
          <b-button variant="primary" class="add-a-child" v-b-modal.add-division-modal>
            <div class="plus">+</div>
            Add a {{ childType }}
          </b-button>
          <!-- TODO: site / brand based on company / brand. Hide for location -->
        </div>
        <div class="grid">
          <EntityCard
            v-for="childSite in children"
            :key="childSite.name"
            :name="childSite.name"
            :numLocations="childSite.number_of_children"
            :tags="childSite.tags"
            :entity-id="childSite.entity_id"
            :cover-url="childSite.cover_image_url"
            :logo-url="childSite.logo_url"
          />
        </div>
      </div>
      <div class="right" v-if="showRightPanel">
        <!-- TODO: make this sub routes? -->
        <RolesSection v-if="showRolesSection" @go-back="onToggleRolesSection" />
        <ReviewsSection :reviews="reviews" v-if="showReviews" @go-back="onToggleReviewsSection" />
      </div>
    </div>
    <EditCompanyModal @on-close="fetchEntity" />
    <EditDivisionsModal @on-close="fetchEntity" />
    <AddDivisionsModal @on-close="fetchEntity" />
  </div>
</template>

<script setup>
import ImagesHeader from '@/pages/talent-pool/location-settings/components/ImagesHeader.vue';
import RolesInfo from '@/pages/talent-pool/location-settings/components/RolesInfo.vue';
import EntityCard from '@/pages/talent-pool/location-settings/components/EntityCard.vue';
import RolesSection from '@/pages/talent-pool/components/screen-sections/RolesSection.vue';
import ApiClient from '@/ApiClient';
import { computed, onMounted, ref } from 'vue';
import BreadcrumbHeader from '@/pages/talent-pool/location-settings/components/BreadcrumbHeader.vue';
import EditCompanyModal from '@/pages/talent-pool/components/EditCompanyModal.vue';
import EditDivisionsModal from '@/pages/talent-pool/components/EditDivisionsModal.vue';
import FeedbackInfo from '@/pages/talent-pool/location-settings/components/FeedbackInfo.vue';
import ReviewsSection from '@/pages/talent-pool/components/screen-sections/ReviewsSection.vue';
import AddDivisionsModal from '@/pages/talent-pool/components/AddDivisionsModal.vue';
import AppLoading from '@/components/AppLoading.vue';
import { getEntityType } from '@/pages/talent-pool/utils/getEntityType';

// TODO: update when tags are ready, pull from entity
const tags = [];

const coverImageUrl = ref('');
const description = ref('');
const logoUrl = ref('');
const name = ref('');
const address = ref({});
const children = ref([]);
const childType = ref('');
const breadcrumbs = ref([]);
const entityType = ref('');

const loading = ref(false);

const showRightPanel = ref(false);
const showRightPanelBar = ref(false);

const roles = ref([]);
const showRolesSection = ref(false);

const reviews = ref([]);
const showReviews = ref(false);

const props = defineProps({
  entityId: String,
});

const addressText = computed(() => {
  return (
    (address?.value?.address_line_1 || '') +
    (address?.value?.city ? `, ${address.value.city}` : '') +
    (address?.value?.postcode ? `, ${address.value.postcode}` : '')
  );
});

const fetchEntity = async () => {
  loading.value = true;

  const { data } = await ApiClient.getTalentPoolEntityDetails(props.entityId);
  loading.value = false;

  coverImageUrl.value = data.cover_image_url;
  name.value = data.name;
  address.value = data.address;
  description.value = data.description;
  entityType.value = getEntityType(data.source);
  logoUrl.value = data.logo_url;
  roles.value = data.roles;
  children.value = data.children.sort((a, b) => a.name.localeCompare(b.name));
  childType.value = entityType.value === 'company' ? 'brand' : 'site';
  breadcrumbs.value = [...data.breadcrumbs].reverse();

  showRightPanelBar.value = entityType.value === 'site';
};

onMounted(async () => {
  await fetchEntity();
});

const onToggleRolesSection = () => {
  if (showRolesSection.value === true) {
    showRolesSection.value = false;
    showRightPanel.value = false;
    return;
  }
  showRolesSection.value = true;
  showReviews.value = false;
  showRightPanel.value = true;
};

const onToggleReviewsSection = () => {
  if (showReviews.value === true) {
    showReviews.value = false;
    showRightPanel.value = false;
    return;
  }
  showReviews.value = true;
  showRightPanel.value = true;
  showRolesSection.value = false;
};
</script>

<style scoped>
.split-view-container {
  min-height: calc(100vh + env(safe-area-inset-bottom) * 1.25);
  display: flex;
  flex-direction: column;
}

.split-view {
  display: flex;
  flex-grow: 1;
  height: 100%;
}

.edit-button {
  svg {
    width: 16px;
    margin-right: 6px;
  }

  svg path {
    fill: var(--palette-color-brand-primary);
  }

  &:hover svg path {
    fill: var(--palette-color-base-white);
  }
}

.number-of-children {
  color: var(--palette-color-default);
  font-weight: 500;
  margin: 0;
}

.add-a-child {
  display: flex;
  align-items: center;
  gap: 10px;

  .plus {
    font-size: 18px;
  }
}

.left {
  padding: 1rem;
  width: 50%;
  background-color: var(--palette-color-base-white);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  border-right: 2px solid var(--palette-color-default-lighten-90);
}

.right {
  width: 50%;
  background-color: var(--palette-color-base-background);
  overflow-y: auto;
  max-height: 90vh;
}

.name {
  font-size: 22px;
  font-weight: 700;
}

.meta-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.inline-header .title {
  font-size: 22px;
  font-weight: 500;
  text-transform: capitalize;
}

.grid {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.description {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  margin-top: 0.5rem;
}

.address {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default);
  text-transform: capitalize;
}

.tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.6rem 0;
}

.tags .tag {
  padding: 2px 1rem;
  background-color: var(--palette-color-default-lighten-95);
  border-radius: 99px;
  font-size: 13px;
  color: var(--palette-color-default-lighten-20);
}

.division-info-container {
  display: flex;
  gap: 10px;

  div {
    flex: 1;
  }
}

@media (max-width: 900px) {
  .split-view {
    display: flex;
    flex-direction: column;
  }

  .left {
    width: 100%;
  }

  .right {
    width: 100%;
  }
}
</style>
